import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';
import { login, setToken, getToken } from './api';

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (getToken()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { access_token } = await login(email.trim(), password);
      setToken(access_token);
      toast.success('Signed in.');
      navigate('/admin', { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen bg-[#001a38] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-[440px]">
        <p className="eyebrow text-[#C5A059] text-center">David Ball Management · Admin</p>
        <h1 className="font-heading text-white uppercase text-4xl md:text-5xl mt-4 leading-[0.95] text-center">
          Sign in
        </h1>

        <form onSubmit={submit} className="mt-12 bg-white rounded-2xl p-8 md:p-10 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.4)]">
          <div>
            <label htmlFor="admin-email" className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">Email</label>
            <input
              id="admin-email"
              data-testid="admin-login-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-black/20 focus:border-[#002b5c] py-2.5 outline-none text-base"
            />
          </div>

          <div className="mt-6">
            <label htmlFor="admin-password" className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">Password</label>
            <input
              id="admin-password"
              data-testid="admin-login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-black/20 focus:border-[#002b5c] py-2.5 outline-none text-base"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-10 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#002b5c] text-white px-9 py-4 text-[11px] tracking-[0.24em] uppercase hover:bg-[#001a38] disabled:opacity-60 transition-all"
          >
            {loading ? 'Signing in…' : 'Sign in'} <LogIn size={14} />
          </button>
        </form>

        <p className="mt-8 text-center text-[11px] tracking-[0.22em] uppercase text-white/50">
          Authorised personnel only
        </p>
      </div>
    </div>
  );
}
